import { button, linkButton } from "../../design-system/components/button.ts";
import { card } from "../../design-system/components/card.ts";
import { confirmDialog } from "../../design-system/components/dialog.ts";
import {
  emptyState,
  errorState,
  loadingBlock,
  notice,
  statusBadge,
} from "../../design-system/components/feedback.ts";
import { field } from "../../design-system/components/field.ts";
import { ICON_NAMES, icon } from "../../design-system/components/icon.ts";
import { segmented } from "../../design-system/components/segmented.ts";
import { stepIndicator } from "../../design-system/components/steps.ts";
import { el, replaceChildren } from "../../design-system/dom.ts";
import { TRANSFER_STATUS_ORDER } from "../../design-system/format/status.ts";
import type { RouteView } from "../router.ts";
import type { PresentationMode, ShellState } from "../state.ts";
import { TENANT_BRAND_PROPERTIES, TENANTS } from "../tenants.ts";

/**
 * Design foundations — the system's own reference page.
 *
 * Kept separate from the product routes on purpose. It documents what exists so
 * the next person building a Nura surface reaches for a token instead of
 * inventing a value, and it is where the tenant switcher lives: re-theming is a
 * property of the design system, not a feature of a transfer.
 */

const SWATCHES: readonly (readonly [string, string])[] = [
  ["Brand", "--nura-brand"],
  ["Brand accent", "--nura-brand-accent"],
  ["Canvas", "--nura-canvas"],
  ["Surface", "--nura-surface"],
  ["Sunken", "--nura-surface-sunken"],
  ["Text", "--nura-text"],
  ["Secondary", "--nura-text-secondary"],
  ["Muted", "--nura-text-muted"],
  ["Control border", "--nura-border-control"],
  ["Success", "--nura-success"],
  ["Attention", "--nura-attention"],
  ["Danger", "--nura-danger"],
  ["Info", "--nura-info"],
  ["Focus ring", "--nura-focus-ring"],
];

const TYPE_SCALE: readonly (readonly [string, string])[] = [
  ["Display", "--nura-text-display"],
  ["3xl", "--nura-text-3xl"],
  ["2xl", "--nura-text-2xl"],
  ["xl", "--nura-text-xl"],
  ["lg", "--nura-text-lg"],
  ["md — body", "--nura-text-md"],
  ["base", "--nura-text-base"],
  ["sm", "--nura-text-sm"],
  ["xs", "--nura-text-xs"],
];

const SPACE_SCALE: readonly (readonly [string, string])[] = [
  ["1", "--nura-space-1"],
  ["2", "--nura-space-2"],
  ["3", "--nura-space-3"],
  ["4", "--nura-space-4"],
  ["6", "--nura-space-6"],
  ["8", "--nura-space-8"],
  ["12", "--nura-space-12"],
  ["16", "--nura-space-16"],
];

function specimenGrid(children: readonly Node[]): HTMLElement {
  return el("div", { class: "nura-specimens" }, children);
}

/**
 * Live readout of everything the active tenant controls.
 *
 * The point of the switcher is not that colours change — it is that *only*
 * these change. Brand tokens and product configuration move; type, spacing,
 * every semantic state colour, the focus treatment and the structure of every
 * screen stay exactly where they were.
 */
function tenantReadout(state: ShellState): HTMLElement {
  const brandRow = el("div", { class: "nura-token-chips" });
  const config = el("dl", { class: "nura-detail-list" });

  function sync(): void {
    replaceChildren(
      brandRow,
      TENANT_BRAND_PROPERTIES.map((property) => {
        const chip = el("span", { class: "nura-token-chip__swatch" });
        chip.style.background = `var(${property})`;
        return el("span", { class: "nura-token-chip" }, [
          chip,
          el("code", {
            class: "nura-mono",
            text: property.replace("--nura-brand", "brand") || "brand",
          }),
        ]);
      }),
    );

    const { defaultCurrency, supportedCurrencies } = state.tenant.currency;
    replaceChildren(config, [
      el("div", { class: "nura-detail-list__row" }, [
        el("dt", { class: "nura-detail-list__term", text: "defaultCurrency" }),
        el("dd", { class: "nura-detail-list__value nura-mono", text: defaultCurrency }),
      ]),
      el("div", { class: "nura-detail-list__row" }, [
        el("dt", { class: "nura-detail-list__term", text: "supportedCurrencies" }),
        el("dd", {
          class: "nura-detail-list__value nura-mono",
          text: `[${supportedCurrencies.join(", ")}]`,
        }),
      ]),
      el("div", { class: "nura-detail-list__row" }, [
        el("dt", { class: "nura-detail-list__term", text: "attribution.mode" }),
        el("dd", {
          class: "nura-detail-list__value nura-mono",
          text: state.tenant.attribution,
        }),
      ]),
      el("div", { class: "nura-detail-list__row" }, [
        el("dt", { class: "nura-detail-list__term", text: "Nura mark" }),
        el("dd", {
          class: "nura-detail-list__value",
          text:
            state.tenant.attribution === "nura"
              ? "leads the app bar"
              : state.tenant.attribution === "powered-by-nura"
                ? "footer attribution only"
                : "not shown to customers",
        }),
      ]),
      el("div", { class: "nura-detail-list__row" }, [
        el("dt", { class: "nura-detail-list__term", text: "Selector" }),
        el("dd", {
          class: "nura-detail-list__value",
          text:
            supportedCurrencies.length > 1
              ? `${supportedCurrencies.length} options, in configured order`
              : "hidden — single-currency tenant",
        }),
      ]),
    ]);
  }

  sync();
  state.subscribe(sync);

  return el("div", { class: "nura-stack" }, [brandRow, config]);
}

/**
 * Tenant and presentation switchers.
 *
 * Both are the shared segmented control rather than two hand-written radio
 * groups. They were the same twenty lines twice, and the receiver reference page
 * would have made it three times.
 */
function tenantSwitcher(state: ShellState): HTMLElement {
  return segmented<string>({
    label: "Preview tenant",
    options: TENANTS.map((tenant) => ({ value: tenant.id, label: tenant.name })),
    value: state.tenant.id,
    onChange: (id) => {
      const tenant = TENANTS.find((candidate) => candidate.id === id);
      if (tenant !== undefined) state.setTenant(tenant);
    },
  }).root;
}

function presentationSwitcher(state: ShellState): HTMLElement {
  return segmented<PresentationMode>({
    label: "Presentation mode",
    options: [
      { value: "reference", label: "Reference" },
      { value: "demo", label: "Partner demo" },
    ],
    value: state.presentation,
    onChange: (mode) => {
      state.setPresentation(mode);
    },
  }).root;
}

export function foundationsRoute(state: ShellState): RouteView {
  const errored = field({
    label: "Reference",
    value: "not-a-reference",
    help: "Help text sits under the control and is read with it.",
    error: "Enter the reference exactly as your bank issued it.",
  });

  const dialog = confirmDialog({
    title: "Discard this draft?",
    text: "Confirmation dialogs are used only where an action loses work or cannot be undone.",
    confirmLabel: "Discard",
    tone: "danger",
    onConfirm: () => undefined,
  });

  const node = el("div", { class: "nura-stack" }, [
    el("div", { class: "nura-page-header" }, [
      el("p", { class: "nura-eyebrow", text: "Design system" }),
      el("h1", { class: "nura-page-header__title", text: "Foundations" }),
      el("p", {
        class: "nura-lede",
        text: "The tokens, motif and components every Nura surface is built from. A bank supplies six brand values and its currency configuration; type, spacing, hierarchy, motion, the aperture, every semantic state colour and every contrast-checked pair stay Nura's.",
      }),
    ]),

    card(
      {
        title: "Tenant",
        note: "Nura's reference identity and two fictional institutions. Everything below re-themes; nothing below changes structure.",
        headingLevel: "h2",
      },
      [
        tenantSwitcher(state),
        tenantReadout(state),
        notice({
          tone: "info",
          title: "What a tenant cannot change",
          text: "Semantic state colours, the focus treatment, type, spacing and layout are Nura's and are not overridable. A bank that could restyle danger could make a declined transfer look approved.",
        }),
      ],
    ),

    card(
      {
        title: "Presentation",
        note: "Reference keeps every engineering explanation. Partner demo turns the commentary down — it never turns a disclosure off.",
        headingLevel: "h2",
      },
      [
        presentationSwitcher(state),
        notice({
          tone: "attention",
          title: "What demo mode never hides",
          text: "That bank authorisation and claim-link generation are not implemented stays on screen in both modes. Only the length of the explanation changes, and the notes written for engineers are the part that goes away.",
        }),
      ],
    ),

    card({ title: "Colour", headingLevel: "h2" }, [
      specimenGrid(
        SWATCHES.map(([name, token]) => {
          const chip = el("span", { class: "nura-swatch__chip" });
          chip.style.background = `var(${token})`;
          return el("div", { class: "nura-swatch" }, [
            chip,
            el("span", { class: "nura-swatch__name", text: name }),
            el("code", { class: "nura-mono nura-swatch__token", text: token }),
          ]);
        }),
      ),
    ]),

    card(
      {
        title: "Type",
        note: "System-first stack, tabular figures everywhere. No font is fetched from a third-party origin.",
        headingLevel: "h2",
      },
      TYPE_SCALE.map(([name, token]) => {
        const sample = el("p", { class: "nura-type-row__sample", text: "Transfer 1 234,56" });
        sample.style.fontSize = `var(${token})`;
        return el("div", { class: "nura-type-row" }, [
          sample,
          el("code", { class: "nura-mono nura-type-row__token", text: name }),
        ]);
      }),
    ),

    card({ title: "Space", note: "A 4px base scale.", headingLevel: "h2" }, [
      el(
        "div",
        { class: "nura-stack" },
        SPACE_SCALE.map(([name, token]) => {
          const bar = el("span", { class: "nura-space-row__bar" });
          bar.style.inlineSize = `var(${token})`;
          return el("div", { class: "nura-space-row" }, [
            el("code", { class: "nura-mono nura-space-row__name", text: name }),
            bar,
          ]);
        }),
      ),
    ]),

    card({ title: "Buttons", headingLevel: "h2" }, [
      el("div", { class: "nura-cluster" }, [
        button({ label: "Primary", variant: "primary" }),
        button({ label: "Secondary", variant: "secondary" }),
        button({ label: "Ghost", variant: "ghost" }),
        button({ label: "Danger", variant: "danger" }),
        button({ label: "Brand subtle", variant: "brand-subtle" }),
      ]),
      el("div", { class: "nura-cluster" }, [
        button({ label: "With icon", variant: "secondary", icon: "shield-check" }),
        button({
          label: "Continue",
          variant: "secondary",
          icon: "arrow-right",
          iconPosition: "end",
        }),
        button({ label: "Small", variant: "secondary", size: "sm" }),
        button({ label: "Loading", variant: "primary", loading: true }),
        button({ label: "Disabled", variant: "primary", disabled: true }),
        linkButton({ label: "Link button", href: "/", variant: "secondary" }),
      ]),
    ]),

    card({ title: "Fields", headingLevel: "h2" }, [
      field({ label: "Label", placeholder: "Placeholder", help: "Help text explains the field." })
        .root,
      errored.root,
      field({ label: "Read only", value: "Set by your bank", readOnly: true }).root,
      field({ label: "Disabled", value: "Unavailable", disabled: true }).root,
    ]),

    card(
      {
        title: "Transfer status",
        note: "The frozen §12 status set. Tone never carries the meaning alone — every badge has an icon and a word.",
        headingLevel: "h2",
      },
      [
        el(
          "div",
          { class: "nura-cluster" },
          TRANSFER_STATUS_ORDER.map((status) => statusBadge(status)),
        ),
      ],
    ),

    card({ title: "Notices", headingLevel: "h2" }, [
      notice({
        tone: "info",
        title: "Information",
        text: "Context that is useful but not urgent.",
      }),
      notice({ tone: "success", title: "Approved", text: "Your bank approved this transfer." }),
      notice({ tone: "attention", title: "Attention", text: "Something needs a decision." }),
      notice({ tone: "danger", title: "Declined", text: "Your bank declined this transfer." }),
    ]),

    card({ title: "Progress", headingLevel: "h2" }, [
      stepIndicator({
        steps: ["Amount", "Review", "Status"],
        current: 1,
        label: "Example progress",
      }),
    ]),

    card({ title: "Loading, empty and error", headingLevel: "h2" }, [
      loadingBlock("Example loading region"),
      emptyState({
        title: "Nothing here yet",
        text: "An empty state says what is missing and what to do about it.",
      }),
      errorState({
        title: "Could not load",
        text: "An error state names a recovery path instead of only reporting failure.",
        action: button({ label: "Try again", variant: "secondary" }),
      }),
    ]),

    card({ title: "Dialog", headingLevel: "h2" }, [
      el("div", { class: "nura-cluster" }, [
        button({
          label: "Open confirmation",
          variant: "secondary",
          onClick: () => {
            dialog.open();
          },
        }),
      ]),
      dialog.root,
    ]),

    card(
      {
        title: "Icons",
        note: "Hand-authored, decorative, always beside a label.",
        headingLevel: "h2",
      },
      [
        specimenGrid(
          ICON_NAMES.map((name) =>
            el("div", { class: "nura-swatch" }, [
              el("span", { class: "nura-icon-tile", staticHtml: icon(name, 22) }),
              el("code", { class: "nura-mono nura-swatch__token", text: name }),
            ]),
          ),
        ),
      ],
    ),
  ]);

  return { node };
}
